import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { Elements, PaymentElement, ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeftIcon,
  LockClosedIcon,
  ExclamationCircleIcon,
  DocumentTextIcon,
  CalendarIcon,
  UserIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';

const stripePromise = loadStripe("pk_live_51IuUXbLwpaCHfpZ1EZNNYaQYdXSIrE6NrdA3r7lUQOIq5tsdhC7MaTITIMFb3nW4yxqyppRd8C66QcxWhuoPC8aa00Ow2q0o0z");

function PaymentForm({ clientSecret, invoiceData, invoiceId }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError('');

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message);
      setProcessing(false);
      return;
    }

    try {
      if (clientSecret) {
        const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);
        if (paymentIntent && (paymentIntent.status === 'processing' || paymentIntent.status === 'requires_capture')) {
          setError('Your payment is processing. We will update this page when it completes.');
          setProcessing(false);
          return;
        }
      }
    } catch (e) {
      // Non-fatal; proceed to confirm
    }

    const result = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
      return_url: `${window.location.origin}/payment-confirmation/${invoiceId}`
    });
    if (result.error) {
      setError(result.error.message);
      setProcessing(false);
    } else if (result.paymentIntent?.status === 'succeeded') {
      const firestore = getFirestore();
      await updateDoc(doc(firestore, 'invoices', invoiceId), {
        status: 'paid',
        paidAt: new Date()
      });
      navigate(`/payment-confirmation/${invoiceId}`);
    } else if (result.paymentIntent?.status === 'processing') {
      setError('Payment is processing. You will receive a confirmation once complete.');
      setProcessing(false);
      return;
    }
    setProcessing(false);
  };

  const subtotal = invoiceData.subtotal || invoiceData.amount || 0;
  const taxAmount = invoiceData.taxAmount || 0;
  const baseTotal = invoiceData.total || (subtotal + taxAmount);
  const feePayer = invoiceData.feePayer || 'creator';
  const processingFee = feePayer === 'customer' ? baseTotal * 0.029 : 0;
  const total = baseTotal + processingFee;

  const displayAmount = total?.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-5"
    >
      {/* Payment Summary */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Payment Summary</h3>
        <div className="bg-gray-50/50 dark:bg-[#0D0D0D] border border-gray-100 dark:border-[#222] rounded-xl p-4 space-y-3">
          {invoiceData.lineItems && invoiceData.lineItems.length > 0 ? (
            <div className="space-y-2">
              {invoiceData.lineItems.map((item, index) => {
                const itemAmount = parseFloat(item.amount) || 0;
                const itemRate = parseFloat(item.rate) || 0;
                const isDiscount = itemAmount < 0;

                return (
                  <div key={item.id || index} className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{item.description}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {item.quantity} × {invoiceData.currency} {itemRate.toFixed(2)}
                      </p>
                    </div>
                    <p className={`text-sm font-medium font-mono ${isDiscount ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                      {invoiceData.currency} {itemAmount.toFixed(2)}
                    </p>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {invoiceData.project || invoiceData.gig || 'Service'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {invoiceData.description || 'Professional services'}
                </p>
              </div>
              <p className="text-sm font-medium text-gray-900 dark:text-white font-mono">
                {invoiceData.currency} {subtotal.toFixed(2)}
              </p>
            </div>
          )}

          <div className="border-t border-gray-200 dark:border-[#333] pt-3 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-500 dark:text-gray-400">Subtotal</span>
              <span className="font-mono text-xs text-gray-900 dark:text-white">
                {invoiceData.currency} {subtotal.toFixed(2)}
              </span>
            </div>

            {taxAmount > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {invoiceData.taxType === 'percentage' ? `Tax (${invoiceData.taxRate || 0}%)` : 'Tax'}
                </span>
                <span className="font-mono text-xs text-gray-900 dark:text-white">
                  {invoiceData.currency} {taxAmount.toFixed(2)}
                </span>
              </div>
            )}

            {processingFee > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500 dark:text-gray-400">Processing fee (2.9%)</span>
                <span className="font-mono text-xs text-gray-900 dark:text-white">
                  {invoiceData.currency} {processingFee.toFixed(2)}
                </span>
              </div>
            )}

            <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-[#333]">
              <span className="text-sm font-semibold text-gray-900 dark:text-white">Total</span>
              <span className="text-lg font-semibold text-gray-900 dark:text-white font-mono">
                {invoiceData.currency} {total.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Express Checkout */}
      <div className="space-y-3">
        <ExpressCheckoutElement
          options={{
            buttonHeight: 48,
            buttonTheme: {
              applePay: 'black',
              googlePay: 'black'
            }
          }}
          onConfirm={async (event) => {
            if (!stripe || !elements) return;

            const { error: submitError } = await elements.submit();
            if (submitError) {
              setError(submitError.message);
              return;
            }

            const { error } = await stripe.confirmPayment({
              elements,
              clientSecret,
              confirmParams: {
                return_url: `${window.location.origin}/payment-confirmation/${invoiceId}`,
              },
              redirect: 'if_required'
            });

            if (error) {
              setError(error.message);
            } else {
              const firestore = getFirestore();
              await updateDoc(doc(firestore, 'invoices', invoiceId), {
                status: 'paid',
                paidAt: new Date()
              });
              navigate(`/payment-confirmation/${invoiceId}`);
            }
          }}
        />

        <div className="relative py-3">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200 dark:border-[#333]"></div>
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="px-3 bg-white dark:bg-[#111111] text-gray-400 dark:text-gray-500">Or pay with card</span>
          </div>
        </div>
      </div>

      {/* Payment Information */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Payment Information</h3>
        <div className="bg-white dark:bg-[#1A1A1A] border border-gray-100 dark:border-[#222] rounded-xl p-4">
          <PaymentElement options={{ layout: 'tabs' }} />
        </div>
      </div>

      {/* Error Display */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="p-3 bg-red-50 dark:bg-red-500/10 border border-red-100 dark:border-red-500/20 rounded-xl"
          >
            <div className="flex items-start gap-2">
              <ExclamationCircleIcon className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-red-800 dark:text-red-400">Payment Error</p>
                <p className="text-xs text-red-600 dark:text-red-300 mt-0.5">{error}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Submit Button */}
      <motion.button
        type="submit"
        disabled={!stripe || processing}
        whileHover={{ scale: processing ? 1 : 1.02 }}
        whileTap={{ scale: processing ? 1 : 0.98 }}
        className={`w-full py-3 px-6 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2 ${
          processing
            ? 'bg-gray-200 dark:bg-[#222] text-gray-400 dark:text-gray-500 cursor-not-allowed'
            : 'bg-brand hover:bg-brand-hover text-white'
        }`}
      >
        {processing ? (
          <>
            <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
            <span>Processing...</span>
          </>
        ) : (
          <>
            <LockClosedIcon className="w-4 h-4" />
            <span>Pay {invoiceData.currency} {displayAmount}</span>
          </>
        )}
      </motion.button>
    </motion.form>
  );
}

export default function PaymentPage() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [invoiceData, setInvoiceData] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInvoiceCreator, setIsInvoiceCreator] = useState(false);
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user);
    });
    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    const loadAndCreateIntent = async () => {
      try {
        const firestore = getFirestore();
        const snap = await getDoc(doc(firestore, 'invoices', invoiceId));
        if (!snap.exists()) {
          setError('Invoice not found.');
          setLoading(false);
          return;
        }
        const invoice = snap.data();

        const currentUser = auth.currentUser;
        if (currentUser && invoice.userId === currentUser.uid) {
          setIsInvoiceCreator(true);
        }

        const rawPrice = invoice.total || invoice.price || invoice.amount || '0';
        const numericPrice = parseFloat(rawPrice);
        if (Number.isNaN(numericPrice)) {
          setError('Invoice data has an invalid amount/price.');
          setLoading(false);
          return;
        }

        invoice.amount = numericPrice;
        setInvoiceData(invoice);

        if (invoice.status === 'paid') {
          navigate(`/payment-confirmation/${invoiceId}`);
          return;
        }

        const payload = {
          invoiceId,
          userId: invoice.userId,
          amount: Math.round(numericPrice * 100),
          currency: invoice.currency,
          description: `Payment for invoice ${invoiceId}`,
          metadata: { invoiceId },
        };

        if (invoice.connectedAccountId) {
          payload.connectedAccountId = invoice.connectedAccountId;
        }

        const response = await fetch('https://klorah-fast-server-my8l.onrender.com/api/create-payment-intent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const text = await response.text();
          setError(`Failed to create PaymentIntent: ${text}`);
          setLoading(false);
          return;
        }

        const { clientSecret } = await response.json();
        setClientSecret(clientSecret);
      } catch (err) {
        setError(`Error: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    loadAndCreateIntent();
  }, [invoiceId, navigate, auth]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] dark:bg-black">
        <div className="flex flex-col items-center gap-3">
          <div className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const isCapabilityError = error.toLowerCase().includes('capabilities') ||
      (error.toLowerCase().includes('paymentintent') &&
       (error.toLowerCase().includes('transfers') ||
        error.toLowerCase().includes('crypto_transfers') ||
        error.toLowerCase().includes('legacy_payments')));

    return (
      <div className="min-h-screen bg-[#FAFAFA] dark:bg-black flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-sm w-full bg-white dark:bg-[#111111] rounded-2xl p-8 text-center shadow-sm border border-gray-100 dark:border-[#222]"
        >
          <div className="w-12 h-12 mx-auto mb-4 bg-red-50 dark:bg-red-500/10 rounded-xl flex items-center justify-center">
            <ExclamationCircleIcon className="w-6 h-6 text-red-500" />
          </div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Payment Error</h1>

          {isCapabilityError && isAuthenticated && isInvoiceCreator ? (
            <>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                Your Stripe account needs setup to receive payments.
              </p>
              <div className="space-y-2">
                <motion.button
                  onClick={() => window.open('https://connect.stripe.com/app/express', '_blank')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full py-2.5 px-4 bg-brand hover:bg-brand-hover text-white font-medium rounded-xl transition-all text-sm"
                >
                  Update Stripe Account
                </motion.button>
                <motion.button
                  onClick={() => navigate(-1)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full py-2.5 px-4 bg-gray-100 dark:bg-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#222] text-gray-700 dark:text-gray-200 font-medium rounded-xl transition-all text-sm"
                >
                  Go Back
                </motion.button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                {isCapabilityError && !isInvoiceCreator
                  ? "This payment cannot be processed. Please contact the invoice sender."
                  : error}
              </p>
              <div className="space-y-2">
                <motion.button
                  onClick={() => window.location.reload()}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full py-2.5 px-4 bg-brand hover:bg-brand-hover text-white font-medium rounded-xl transition-all text-sm"
                >
                  Try Again
                </motion.button>
                <motion.button
                  onClick={() => navigate(-1)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full py-2.5 px-4 bg-gray-100 dark:bg-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#222] text-gray-700 dark:text-gray-200 font-medium rounded-xl transition-all text-sm"
                >
                  Go Back
                </motion.button>
              </div>
            </>
          )}
        </motion.div>
      </div>
    );
  }

  if (!invoiceData) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] dark:bg-black flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-sm w-full bg-white dark:bg-[#111111] rounded-2xl p-8 text-center shadow-sm border border-gray-100 dark:border-[#222]"
        >
          <div className="w-12 h-12 mx-auto mb-4 bg-gray-100 dark:bg-[#1A1A1A] rounded-xl flex items-center justify-center">
            <DocumentTextIcon className="w-6 h-6 text-gray-400 dark:text-gray-500" />
          </div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Invoice Not Found</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            This invoice may have been deleted or moved.
          </p>
          <motion.button
            onClick={() => navigate('/')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full py-2.5 px-4 bg-brand hover:bg-brand-hover text-white font-medium rounded-xl transition-all text-sm"
          >
            Go Home
          </motion.button>
        </motion.div>
      </div>
    );
  }

  const baseSubtotalMain = invoiceData?.subtotal || invoiceData?.amount || 0;
  const baseTaxAmountMain = invoiceData?.taxAmount || 0;
  const baseTotalMain = invoiceData?.total || (baseSubtotalMain + baseTaxAmountMain);
  const feePayerMain = invoiceData?.feePayer || 'creator';
  const feeMultiplierMain = (feePayerMain === 'customer') ? 1.029 : 1;
  const totalMain = baseTotalMain * feeMultiplierMain;

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-black">
      {/* Header - Fixed */}
      <div className="sticky top-0 z-20 bg-[#FAFAFA]/80 dark:bg-black/80 backdrop-blur-xl border-b border-gray-200/50 dark:border-[#222]">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 h-14 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Back</span>
          </button>

          <h1 className="text-lg font-bold tracking-[0.3em] text-gray-900 dark:text-white">KLORAH</h1>

          <div className="w-12" />
        </div>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-4 lg:py-6">
        <div className="flex flex-col lg:flex-row gap-6">

          {/* Left Column - Payment Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex-1 order-2 lg:order-1"
          >
            <div className="bg-white dark:bg-[#111111] rounded-2xl border border-gray-100 dark:border-[#222] shadow-sm overflow-hidden p-5 lg:p-6">
              {clientSecret ? (
                <Elements
                  stripe={stripePromise}
                  options={{
                    clientSecret,
                    appearance: {
                      theme: 'stripe',
                      variables: {
                        colorPrimary: '#f97316',
                        colorBackground: 'transparent',
                        colorText: '#111827',
                        colorDanger: '#EF4444',
                        fontFamily: 'system-ui, sans-serif',
                        spacingUnit: '4px',
                        borderRadius: '12px',
                        fontSizeBase: '16px'
                      },
                      rules: {
                        '.Tab': {
                          borderRadius: '12px',
                          border: '1px solid #E5E7EB',
                          padding: '16px',
                          backgroundColor: 'white'
                        },
                        '.Tab--selected': {
                          borderColor: '#f97316',
                          backgroundColor: '#FDF2F4'
                        },
                        '.Input': {
                          borderRadius: '8px',
                          border: '1px solid #E5E7EB',
                          padding: '12px',
                          fontSize: '16px'
                        },
                        '.Input:focus': {
                          borderColor: '#f97316',
                          boxShadow: '0 0 0 2px rgba(249, 115, 22, 0.1)'
                        }
                      }
                    },
                    loader: 'auto'
                  }}
                >
                  <PaymentForm
                    clientSecret={clientSecret}
                    invoiceData={invoiceData}
                    invoiceId={invoiceId}
                  />
                </Elements>
              ) : (
                <div className="text-center py-8">
                  <div className="w-12 h-12 mx-auto mb-4 bg-gray-100 dark:bg-[#1A1A1A] rounded-xl flex items-center justify-center">
                    <ExclamationCircleIcon className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                  </div>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Payment Unavailable</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">No payment details found. Please try again.</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Right Column - Invoice Summary (Sticky Sidebar) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="w-full lg:w-80 xl:w-96 order-1 lg:order-2 flex-shrink-0"
          >
            <div className="lg:sticky lg:top-20">
              <div className="bg-white dark:bg-[#111111] rounded-2xl border border-gray-100 dark:border-[#222] shadow-sm overflow-hidden">

                {/* Status & Title */}
                <div className="p-5 border-b border-gray-100 dark:border-[#222]">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <h1 className="text-lg font-semibold text-gray-900 dark:text-white leading-tight">
                      {invoiceData.project || invoiceData.gig || 'Invoice Payment'}
                    </h1>
                    <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 flex-shrink-0">
                      Due
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {invoiceData.clientName || invoiceData.client || invoiceData.customer || 'Client'}
                  </p>
                </div>

                {/* Amount - Prominent */}
                <div className="p-5 bg-gray-50/50 dark:bg-[#0D0D0D] border-b border-gray-100 dark:border-[#222]">
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Amount Due</p>
                  <p className="text-3xl font-semibold text-gray-900 dark:text-white">
                    {invoiceData.currency || 'USD'} {totalMain.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>

                {/* Details Grid */}
                <div className="p-5 border-b border-gray-100 dark:border-[#222] space-y-3">
                  {invoiceData.dueDate && (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center flex-shrink-0">
                        <CalendarIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Due Date</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {new Date(invoiceData.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                  )}

                  {invoiceData.fromName && (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center flex-shrink-0">
                        <UserIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">From</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{invoiceData.fromName}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center flex-shrink-0">
                      <CreditCardIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Invoice ID</p>
                      <p className="text-xs font-mono text-gray-900 dark:text-white">{invoiceId}</p>
                    </div>
                  </div>
                </div>

                {/* Security Notice */}
                <div className="p-4">
                  <div className="flex items-center justify-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
                    <LockClosedIcon className="w-3.5 h-3.5" />
                    <span>Secure payment powered by Stripe</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
